import { useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { ActionButtons } from './ActionButtons';
import { ChipBanner } from './ChipBanner';
import { ChipModal } from './ChipModal';
import { Header } from './Header';
import { PlayerListPanel } from './PlayerListPanel';
import { BuildTeam } from './BuildTeam';
import { Scoreboard } from './Scoreboard';
import { ResetTeamModal } from './ResetTeamModal';
import { UpdateBankModal } from './UpdateBankModal';
import { TransferCommitBar } from './TransferCommitBar';
import {
  FREE_PREDICTIONS_GAMEWEEK,
  RECOMMEND_TRANSFER_PLACEMENT
} from './constants';

// FFH-2373: the two surfaces that open the player info modal on this page.
const PLAYER_INFO_SOURCE_PITCH = 'pick_pitch';
const PLAYER_INFO_SOURCE_PLAYER_LIST = 'pick_player_list';
import { TransferErrorModal } from './TransferErrorModal';
import { TransferHitModal } from './TransferHitModal';
import { ViewSelector } from './ViewSelector';
import type { PlayerInfoSubject } from '@components/player/PlayerInfoModal';
import { PlayerInfoModal } from '@components/player/PlayerInfoModal';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { useCustomTransfersStore } from '@lib/transfers/store/useCustomTransfersStore';
import { TeamImportModal } from '@components/my-team/TeamImportModal';
import TeamLineup from '@components/team-lineup';
import Button from '@components/atoms/button';
import { WarningAlert } from '@components/alert';
import LoadingView from '@components/atoms/loading-view';
import EndOfSeasonHoldingMessage from '@components/end-of-season-holding-message';
import ErrorHoldingMessage from '@components/error-holding-message';
import { useMyTeamPickState } from '@hooks/my-team/useMyTeamPickState';
import type { PickViewMode } from '@hooks/my-team/useMyTeamPickState';
import { useMyTeamPriceChanges } from '@hooks/my-team/useMyTeamPriceChanges';
import { useLeagueContext } from '@hooks/useLeagueContext';
import { useProfile } from '@hooks/useProfile';
import { useImportFromFpl } from '@hooks/my-team/useImportFromFpl';
import { usePlannerMutations } from '@hooks/my-team/usePlannerMutations';
import { useTransferMode } from '@hooks/my-team/useTransferMode';
import { useIsAppView } from '@hooks/useIsAppView';
import {
  buildChipSelections,
  buildGameweekOptionsForChip
} from '@lib/my-team/utils/chip-strategy';
import {
  derivePickViewState,
  isReadyState
} from '@lib/my-team/utils/pick-view-state';
import { resolveSwapCaptaincy } from '@lib/my-team/utils/swap-captaincy';
import { deriveTransferHit } from '@lib/my-team/utils/transfer-hit';
import {
  findClubOverflow,
  MAX_PLAYERS_PER_CLUB
} from '@lib/my-team/utils/club-overflow';
import {
  isPlannerConflict,
  PlannerApiError,
  type ChipType,
  type PlannerBenchPlayer,
  type PlannerGameweek,
  type PlannerSquadPlayer,
  type TransferEvent
} from '@api-service/planner';
import type { Player } from '@api-service/players';
import type { LineupPlayer } from '@components/team-lineup';

const VIEW_MODE_FIXTURE_COUNT: Record<PickViewMode, number> = {
  nextGw: 1,
  next3Gw: 3,
  priceChanges: 3
};

const STARTING_XI_MAX_SLOT = 11;
const BENCH_SLOT_OFFSET = 12;

type SlottedPlayer = {
  player: PlannerSquadPlayer | PlannerBenchPlayer;
  slot: number;
};

export type ActionTarget = SlottedPlayer;

/** Squad player as the info modal wants it: no planner shape, no slot. */
const toInfoSubject = (
  player: PlannerSquadPlayer | PlannerBenchPlayer
): PlayerInfoSubject => ({
  id: player.id,
  displayName: player.displayName,
  position: player.position,
  teamShortName: player.team.shortName,
  price: player.price,
  status: player.status,
  chanceOfPlaying: player.chanceOfPlaying,
  seedFixtures: player.fixtures.map((fixture) => ({
    gameweek: fixture.gameweek,
    opponent: { shortName: fixture.opponent.shortName },
    isHome: fixture.isHome,
    predictedPoints: fixture.predictedPoints
  }))
});

/** Same, for a player picked out of the player list rather than the squad. */
const listPlayerToInfoSubject = (player: Player): PlayerInfoSubject => ({
  id: player.id,
  displayName: player.displayName,
  position: player.position,
  teamShortName: player.team.shortName,
  price: player.price,
  status: player.status,
  chanceOfPlaying: player.chanceOfPlaying,
  seedFixtures: player.fixtures.map((fixture) => ({
    gameweek: fixture.gameweek,
    opponent: { shortName: fixture.opponent.shortName },
    isHome: fixture.isHome,
    predictedPoints: fixture.predictions?.points ?? null
  }))
});

const buildSlotIndex = (
  gameweekData: PlannerGameweek
): Map<string, SlottedPlayer> => {
  const slotIndex = new Map<string, SlottedPlayer>();
  gameweekData.starting.forEach((player, index) => {
    slotIndex.set(player.id, { player, slot: index + 1 });
  });
  gameweekData.bench.forEach((player, index) => {
    slotIndex.set(player.id, { player, slot: index + BENCH_SLOT_OFFSET });
  });
  return slotIndex;
};

const isStartingSlot = (slot: number) => slot <= STARTING_XI_MAX_SLOT;

const toLineupPlayer = (
  player: PlannerSquadPlayer | PlannerBenchPlayer
): LineupPlayer => ({
  id: player.id,
  displayName: player.displayName,
  position: player.position,
  team: { shortName: player.team.shortName },
  kitImageUrl: player.kitImageUrl,
  // The tile shows what the player is worth to the user: their sell value.
  price: player.sellingPrice,
  fixtures: player.fixtures.map((fixture) => ({
    gameweek: fixture.gameweek,
    opponent: { shortName: fixture.opponent.shortName },
    isHome: fixture.isHome,
    predictedPoints: fixture.predictedPoints
  })),
  isCaptain: player.isCaptain,
  isViceCaptain: player.isViceCaptain,
  status: player.status,
  chanceOfPlaying: player.chanceOfPlaying
});

const incomingPlayerToLineup = (incoming: Player): LineupPlayer => ({
  id: incoming.id,
  displayName: incoming.displayName,
  position: incoming.position,
  team: { shortName: incoming.team.shortName },
  kitImageUrl: incoming.kitImageUrl,
  price: incoming.price,
  fixtures: incoming.fixtures.map((fixture) => ({
    gameweek: fixture.gameweek,
    opponent: { shortName: fixture.opponent.shortName },
    isHome: fixture.isHome,
    predictedPoints: fixture.predictions?.points ?? null
  })),
  isCaptain: false,
  isViceCaptain: false,
  status: incoming.status,
  chanceOfPlaying: incoming.chanceOfPlaying
});

type PickProps = {
  /**
   * Paying members get the full planner. Non-paying members can build, edit
   * and browse their team across gameweeks, with the team rating and the
   * free gameweek's predictions visible; every other gameweek's predictions,
   * chip strategy, optimise and transfer insights show padlocks that open
   * the upgrade modal.
   */
  isPayingMember?: boolean;
};

export const Pick = ({ isPayingMember = true }: PickProps) => {
  const isLocked = !isPayingMember;
  const router = useRouter();
  const initialiseFromRecommendedPlayer = useCustomTransfersStore(
    (state) => state.initialiseFromRecommendedPlayer
  );
  const {
    isLoading,
    isError,
    isFetching,
    isSeasonClosed,
    errorStatusCode,
    refetch,
    hasTeam,
    planner,
    selectedGameweek,
    setSelectedGameweek,
    selectedGameweekData,
    availableGameweeks,
    viewMode,
    setViewMode
  } = useMyTeamPickState();

  const { isAppView } = useIsAppView();
  const { profile } = useProfile();
  const { context } = useLeagueContext();
  // The custom transfers builder only ever offers the next gameweek's squad as
  // players out, so recommending from any other gameweek would seed an id it
  // cannot render (a player transferred in by a later planned gameweek).
  // Declared above the view-state returns below to keep hook order stable.
  const nextGameweekSquadIds = useMemo(() => {
    const gameweek = planner?.data.gameweeks.find(
      (entry) => entry.gameweek === context?.nextGameweek?.number
    );
    if (!gameweek) return new Set<string>();
    return new Set(
      [...gameweek.starting, ...gameweek.bench].map((player) => player.id)
    );
  }, [planner, context?.nextGameweek?.number]);

  const isPreGameweekOne = context?.seasonStatus === 'PRE_SEASON';
  const {
    swap,
    setCaptain,
    commitTransfers,
    activateChip,
    deactivateChip,
    optimiseLineup,
    setBank
  } = usePlannerMutations();
  const importFromFpl = useImportFromFpl();

  const transferMode = useTransferMode(selectedGameweekData?.bank ?? 0);

  // Staged transfers land on top of whatever the gameweek has already spent,
  // so the warning reflects the total the member would end up having made.
  const transferHit = deriveTransferHit(
    selectedGameweekData?.transfers.free,
    (selectedGameweekData?.transfers.made ?? 0) +
      transferMode.stagedTransfers.length
  );

  const [actionTarget, setActionTarget] = useState<ActionTarget | null>(null);
  // A player opened from the player list rather than the pitch: same modal,
  // no squad actions, because they are not in the squad.
  const [infoOnlyPlayer, setInfoOnlyPlayer] = useState<Player | null>(null);
  const [swapSource, setSwapSource] = useState<ActionTarget | null>(null);
  const [selectedChipType, setSelectedChipType] = useState<ChipType | null>(
    null
  );
  const [isResetTeamOpen, setIsResetTeamOpen] = useState(false);
  const [isEditBankOpen, setIsEditBankOpen] = useState(false);
  const [isImportTeamOpen, setIsImportTeamOpen] = useState(false);
  const [isRebuildingTeam, setIsRebuildingTeam] = useState(false);
  const [isEditModeActive, setIsEditModeActive] = useState(false);
  const [transferBlockMessage, setTransferBlockMessage] = useState<
    string | null
  >(null);
  // Transfers held back while the hit warning is on screen, so confirming
  // commits exactly the set that was validated rather than rebuilding it.
  const [pendingHitTransfers, setPendingHitTransfers] = useState<
    TransferEvent[] | null
  >(null);
  const playerListRef = useRef<HTMLDivElement | null>(null);

  const handleToggleEditTeam = () => {
    setIsEditModeActive((current) => !current);
    if (!isEditModeActive) {
      playerListRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const slotIndex = useMemo(
    () => (selectedGameweekData ? buildSlotIndex(selectedGameweekData) : null),
    [selectedGameweekData]
  );

  const priceChangeSquadIds = useMemo(
    () =>
      selectedGameweekData
        ? [
            ...selectedGameweekData.starting.map((player) => player.id),
            ...selectedGameweekData.bench.map((player) => player.id)
          ]
        : [],
    [selectedGameweekData]
  );

  const { priceChangeMap, isLoading: isPriceChangeLoading } =
    useMyTeamPriceChanges(priceChangeSquadIds, viewMode);

  const viewState = derivePickViewState({
    isLoading,
    isError,
    hasTeam,
    isSeasonClosed
  });

  if (viewState.status === 'seasonClosed') {
    return (
      <EndOfSeasonHoldingMessage
        pageTitle="My Team Pick"
        isAppView={isAppView}
      />
    );
  }

  if (viewState.status === 'loading') {
    return (
      <div
        className="flex justify-center h-[calc(100vh-175px)]"
        data-cy="pick-loading"
      >
        <LoadingView
          headerText="Loading"
          loadingText="Fetching your team"
          progressBarSeconds={8}
        />
      </div>
    );
  }

  if (viewState.status === 'error') {
    return (
      <div data-cy="pick-error">
        <ErrorHoldingMessage
          pageTitle="your team"
          statusCode={errorStatusCode}
          onRetry={refetch}
          isRetrying={isFetching}
        />
      </div>
    );
  }

  if (viewState.status === 'noTeam') {
    // Pre-gameweek-one: FPL has no authoritative squad yet, so let the user
    // build one (screenshot import or manual). Once GW1 is underway we fall
    // back to importing the authoritative squad from FPL by ID.
    if (isPreGameweekOne) {
      return <BuildTeam isLocked={isLocked} />;
    }

    return (
      <div className="pt-5 flex flex-col gap-3" data-cy="pick-no-team">
        <WarningAlert text="Please set up your team to start using the Pick tab." />
        <Button
          variant="primary"
          onClick={() => setIsImportTeamOpen(true)}
          data-cy="action-import"
        >
          Import from FPL
        </Button>
        <TeamImportModal
          isOpen={isImportTeamOpen}
          onClose={() => setIsImportTeamOpen(false)}
          onImport={(fplTeamId) => importFromFpl.mutateAsync(fplTeamId)}
          isImporting={importFromFpl.isLoading}
          title="Import your FPL team"
          description="Enter your FPL ID or search by team name to import your squad."
        />
      </div>
    );
  }

  if (
    !isReadyState(viewState) ||
    !selectedGameweekData ||
    selectedGameweek == null ||
    !slotIndex
  ) {
    return null;
  }

  // Pre-gameweek-one reset drops the user into the build flow (screenshot or
  // manual). Saving overwrites the old team via the planner upsert; cancelling
  // keeps it untouched.
  if (isRebuildingTeam) {
    return (
      <BuildTeam
        isLocked={isLocked}
        onTeamSaved={() => setIsRebuildingTeam(false)}
        onCancel={() => setIsRebuildingTeam(false)}
      />
    );
  }

  const isInTransferMode =
    isEditModeActive || transferMode.stagedTransfers.length > 0;
  // Mirrors the app's gate: only the stored amount for the upcoming gameweek
  // can be overridden, never a live or past week, and never GW1 where the
  // bank comes straight from the imported team. Editing is also hidden while
  // transfers are staged, since the header then shows a projection.
  const nextGameweekNumber = context?.nextGameweek?.number ?? null;
  const canEditBank =
    nextGameweekNumber != null &&
    nextGameweekNumber > 1 &&
    selectedGameweek === nextGameweekNumber &&
    !isInTransferMode;
  const isMutationPending =
    swap.isLoading ||
    setCaptain.isLoading ||
    commitTransfers.isLoading ||
    activateChip.isLoading ||
    deactivateChip.isLoading ||
    optimiseLineup.isLoading ||
    importFromFpl.isLoading;

  // Keep this resolution order in step with `mutationError` below so the title
  // always describes the same action that produced the surfaced error.
  // importFromFpl errors are deliberately absent: the reset and import modals
  // surface those inline, so routing them here too would stack a second error
  // modal on top.
  const mutationErrorSource = swap.error
    ? 'swap'
    : setCaptain.error
    ? 'captain'
    : commitTransfers.error
    ? 'transfer'
    : activateChip.error || deactivateChip.error
    ? 'chip'
    : optimiseLineup.error
    ? 'optimise'
    : null;
  const mutationError =
    swap.error ??
    setCaptain.error ??
    commitTransfers.error ??
    activateChip.error ??
    deactivateChip.error ??
    optimiseLineup.error;
  const violations =
    isPlannerConflict(mutationError) && mutationError.violations
      ? mutationError.violations
      : [];
  const GENERIC_MUTATION_ERROR_MESSAGE =
    'Something went wrong. Please try again.';
  const errorMessages =
    mutationError instanceof PlannerApiError
      ? violations.length > 0
        ? violations.map((violation) => violation.message)
        : [mutationError.message]
      : mutationError instanceof Error
      ? [mutationError.message || GENERIC_MUTATION_ERROR_MESSAGE]
      : [];
  // A blocked substitution is a SWAP, not a transfer, so the modal title must
  // reflect the action the user actually took rather than always saying
  // "Transfer not allowed".
  const VIOLATION_TITLE_BY_SOURCE: Record<string, string> = {
    swap: 'Substitution not allowed',
    transfer: 'Transfer not allowed'
  };
  const errorTitle =
    violations.length > 0
      ? VIOLATION_TITLE_BY_SOURCE[mutationErrorSource ?? ''] ??
        'Action not allowed'
      : 'Something went wrong';

  const clearMutationErrors = () => {
    swap.reset();
    setCaptain.reset();
    commitTransfers.reset();
    activateChip.reset();
    deactivateChip.reset();
    optimiseLineup.reset();
    importFromFpl.reset();
  };

  const findCurrentCaptain = () =>
    selectedGameweekData.starting.find((player) => player.isCaptain) ?? null;
  const findCurrentViceCaptain = () =>
    selectedGameweekData.starting.find((player) => player.isViceCaptain) ??
    null;

  const handlePlayerPress = (playerId: string) => {
    const target = slotIndex.get(playerId);
    if (!target) return;

    if (swapSource) {
      if (target.slot === swapSource.slot) {
        setSwapSource(null);
        return;
      }
      // Keep the captain in the XI when subbing them out by handing the armband
      // to the incoming player in the same request (see resolveSwapCaptaincy).
      swap.mutate({
        gameweek: selectedGameweek,
        slots: [swapSource.slot, target.slot],
        captaincy: resolveSwapCaptaincy(
          swapSource,
          target,
          findCurrentCaptain()?.id ?? null,
          findCurrentViceCaptain()?.id ?? null
        )
      });
      setSwapSource(null);
      return;
    }

    setActionTarget(target);
    // FFH-2373: only reached when the press actually opens the info modal —
    // the swap-mode branches above all return early.
    captureAnalyticsEvent(AnalyticsEvents.playerInfoOpened, {
      source: PLAYER_INFO_SOURCE_PITCH
    });
  };

  const handleShowPlayerInfo = (player: Player) => {
    captureAnalyticsEvent(AnalyticsEvents.playerInfoOpened, {
      source: PLAYER_INFO_SOURCE_PLAYER_LIST
    });
    setInfoOnlyPlayer(player);
  };

  const handleMakeCaptain = (target: ActionTarget) => {
    if (!isStartingSlot(target.slot)) {
      setActionTarget(null);
      return;
    }
    const currentCaptain = findCurrentCaptain();
    if (!currentCaptain) {
      setActionTarget(null);
      return;
    }
    const currentViceCaptain = findCurrentViceCaptain();
    const isCurrentViceCaptain = currentViceCaptain?.id === target.player.id;
    const newViceCaptainId = isCurrentViceCaptain
      ? currentCaptain.id
      : currentViceCaptain?.id ?? currentCaptain.id;

    setCaptain.mutate({
      gameweek: selectedGameweek,
      captainId: target.player.id,
      viceCaptainId: newViceCaptainId
    });
    setActionTarget(null);
  };

  const handleMakeViceCaptain = (target: ActionTarget) => {
    if (!isStartingSlot(target.slot)) {
      setActionTarget(null);
      return;
    }
    const currentCaptain = findCurrentCaptain();
    if (!currentCaptain) {
      setActionTarget(null);
      return;
    }
    const isCurrentCaptain = currentCaptain.id === target.player.id;
    if (isCurrentCaptain) {
      const currentViceCaptain = findCurrentViceCaptain();
      if (!currentViceCaptain) {
        setActionTarget(null);
        return;
      }
      setCaptain.mutate({
        gameweek: selectedGameweek,
        captainId: currentViceCaptain.id,
        viceCaptainId: target.player.id
      });
      setActionTarget(null);
      return;
    }
    setCaptain.mutate({
      gameweek: selectedGameweek,
      captainId: currentCaptain.id,
      viceCaptainId: target.player.id
    });
    setActionTarget(null);
  };

  const handleStartSubstitution = (target: ActionTarget) => {
    setSwapSource(target);
    setActionTarget(null);
  };

  const handleTransferOut = (target: ActionTarget) => {
    transferMode.transferOut(target.slot, target.player);
    setActionTarget(null);
  };

  // Seeding the custom builder is all the transfers page needs: the store
  // marks the filters applied, so suggestions generate on arrival.
  const handleRecommendTransfer = (target: ActionTarget) => {
    captureAnalyticsEvent(AnalyticsEvents.transferRecommendationRequested, {
      placement: RECOMMEND_TRANSFER_PLACEMENT,
      player_id: target.player.id
    });
    initialiseFromRecommendedPlayer(target.player.id);
    setActionTarget(null);
    router.push('/my-team/transfers');
  };

  // The chip is scheduled for the gameweek chosen in the modal, which may
  // differ from the gameweek currently being viewed. A null gameweek is the
  // "not set" option: the chip is unscheduled from wherever it currently sits,
  // which is the week the backend needs naming to clear it.
  const handleSaveChip = (gameweek: number | null) => {
    if (!selectedChipType) return;
    const closeOnSuccess = { onSuccess: () => setSelectedChipType(null) };

    if (gameweek === null) {
      const scheduledGameweek =
        chipSelections[selectedChipType].selectedGameweek;
      if (scheduledGameweek === null) {
        setSelectedChipType(null);
        return;
      }

      deactivateChip.mutate(
        { gameweek: scheduledGameweek, chip: selectedChipType },
        closeOnSuccess
      );
      return;
    }

    activateChip.mutate({ gameweek, chip: selectedChipType }, closeOnSuccess);
  };

  const handleOptimise = () => {
    optimiseLineup.mutate({ gameweek: selectedGameweek });
  };

  const handleResetTeamImport = async (fplTeamId: number) => {
    await importFromFpl.mutateAsync(fplTeamId);
  };

  // Before gameweek one FPL has no authoritative squad to import by ID, so
  // reset re-enters the build flow instead of the reset modal. In season the
  // primary reset restores the gameweek-start squad using the FPL ID already
  // on the profile; users without a stored ID (e.g. a manually built team)
  // have no FPL squad to restore, so they go straight to the import path.
  const hasStoredFplTeamId = profile?.fplTeamId != null;

  const handleResetTeam = () => {
    if (isPreGameweekOne) {
      setIsRebuildingTeam(true);
      return;
    }
    if (!hasStoredFplTeamId) {
      setIsImportTeamOpen(true);
      return;
    }
    setIsResetTeamOpen(true);
  };

  const handleConfirmReset = async () => {
    await importFromFpl.mutateAsync(undefined);
  };

  const handleCommitTransfers = () => {
    // Catch a breach of the per-club limit before hitting the API, so the user
    // gets a clear message instead of a generic backend rejection.
    const overflow = findClubOverflow(
      [...selectedGameweekData.starting, ...selectedGameweekData.bench],
      transferMode.stagedTransfers
    );
    if (overflow) {
      setTransferBlockMessage(
        `You can only have ${MAX_PLAYERS_PER_CLUB} players from one club. This change would leave you with ${overflow.count} from ${overflow.teamShortName}.`
      );
      return;
    }

    const events = transferMode.buildTransferEvents(selectedGameweek);

    // Warn before committing transfers that exceed the free allowance and
    // would incur a points hit at the deadline (FFH-2998).
    if (transferHit.exceedsFree) {
      setPendingHitTransfers(events);
      return;
    }

    commitStagedTransfers(events);
  };

  const commitStagedTransfers = (events: TransferEvent[]) => {
    commitTransfers.mutate(
      { transfers: events },
      {
        onSuccess: () => {
          setPendingHitTransfers(null);
          transferMode.reset();
          setIsEditModeActive(false);
        },
        // Clearing on failure too, so a rejected commit returns the member to
        // the staged squad rather than stranding them behind the warning.
        onError: () => setPendingHitTransfers(null)
      }
    );
  };

  // Commits exactly the set the warning priced. The modal stays up until the
  // mutation settles so the confirm button can show it is in flight.
  const handleConfirmHit = () => {
    if (pendingHitTransfers) commitStagedTransfers(pendingHitTransfers);
  };

  // Gameweeks up to and including the live gameweek are locked: chips played
  // there are spent and cannot be rescheduled.
  const lockedThroughGameweek = context?.currentGameweek?.number ?? 0;
  const chipSelections = buildChipSelections(
    planner?.data.chips ?? [],
    lockedThroughGameweek
  );
  const chipGameweekOptions = selectedChipType
    ? buildGameweekOptionsForChip({
        chipType: selectedChipType,
        selections: chipSelections,
        availableGameweeks,
        lockedThroughGameweek
      })
    : [];

  const squadPlayerIds = [
    ...selectedGameweekData.starting.map((player) => player.id),
    ...selectedGameweekData.bench.map((player) => player.id)
  ];
  const transferBySlot = new Map(
    transferMode.stagedTransfers.map((transfer) => [transfer.slot, transfer])
  );
  const stagedOutgoingIds = transferMode.stagedTransfers
    .filter((transfer) => transfer.incoming === null)
    .map((transfer) => transfer.outgoing.id);
  const stagedIncomingIds = transferMode.stagedTransfers
    .map((transfer) => transfer.incoming?.id)
    .filter((id): id is string => Boolean(id));
  // Everyone the user already holds: the squad, whoever is on their way out,
  // and anyone staged as an incoming replacement. They stay visible in the
  // player list but can't be picked while a replacement is pending.
  const disabledPlayerIds = [
    ...squadPlayerIds,
    ...transferMode.stagedTransfers.map((transfer) => transfer.outgoing.id),
    ...stagedIncomingIds
  ];

  const composeSlot = (
    player: PlannerSquadPlayer | PlannerBenchPlayer,
    slot: number
  ): LineupPlayer => {
    const transfer = transferBySlot.get(slot);
    if (transfer?.incoming) return incomingPlayerToLineup(transfer.incoming);
    return toLineupPlayer(player);
  };

  const withPriceChange = (player: LineupPlayer): LineupPlayer =>
    player.id
      ? { ...player, priceChange: priceChangeMap.get(player.id) }
      : player;

  const displayedStarting = selectedGameweekData.starting.map((player, index) =>
    withPriceChange(composeSlot(player, index + 1))
  );
  const displayedBench = selectedGameweekData.bench.map((player, index) =>
    withPriceChange(composeSlot(player, index + BENCH_SLOT_OFFSET))
  );

  const isPickingReplacement = transferMode.pendingIncomingForSlot != null;

  const handleQuickCaptain = (playerId: string) => {
    const target = slotIndex.get(playerId);
    if (target) handleMakeCaptain(target);
  };

  const handleQuickViceCaptain = (playerId: string) => {
    const target = slotIndex.get(playerId);
    if (target) handleMakeViceCaptain(target);
  };

  const handleQuickTransferOut = (playerId: string) => {
    const target = slotIndex.get(playerId);
    if (target) transferMode.transferOut(target.slot, target.player);
  };

  const handleCancelTransferOut = (playerId: string) => {
    // The incoming player keeps its real id, so cancelling could fire on
    // either the outgoing-still-on-pitch player or the incoming replacement.
    // Look up the staged transfer in either direction.
    const transfer = transferMode.stagedTransfers.find(
      (entry) =>
        entry.outgoing.id === playerId || entry.incoming?.id === playerId
    );
    if (transfer) transferMode.cancelTransferOut(transfer.slot);
  };

  return (
    <div className="flex flex-col gap-4 pt-2" data-cy="pick-ready">
      {/* One bordered card: the deadline governs the stats beneath it, so
          the scoreboard and stat band share a shell. overflow-hidden clips
          the stat band's tint and dividers to the radius. */}
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <Scoreboard
          gameweeks={availableGameweeks}
          selectedGameweek={selectedGameweek}
          selectedGameweekDeadline={selectedGameweekData.deadlineAt}
          onSelectGameweek={setSelectedGameweek}
          isLocked={isInTransferMode}
        />
        <Header
          gameweekData={selectedGameweekData}
          projectedBank={
            transferMode.stagedTransfers.length > 0
              ? transferMode.projectedBank
              : null
          }
          pendingTransferCount={transferMode.stagedTransfers.length}
          isPredictedLocked={
            isLocked && selectedGameweek !== FREE_PREDICTIONS_GAMEWEEK
          }
          isTransfersLocked={isLocked}
          onEditBank={canEditBank ? () => setIsEditBankOpen(true) : undefined}
        />
      </div>

      {swapSource && (
        <div
          className="flex items-center justify-between gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-lg"
          data-cy="swap-banner"
        >
          <span className="text-sm text-neutral-800">
            Choose a player to swap with{' '}
            <strong>{swapSource.player.displayName}</strong>.
          </span>
          <Button
            variant="secondary"
            onClick={() => setSwapSource(null)}
            data-cy="swap-cancel"
          >
            Cancel
          </Button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <div className="flex flex-col gap-4">
          <div className="flex justify-center">
            <ViewSelector
              viewMode={viewMode}
              onViewModeChange={setViewMode}
              shouldShowPriceChanges={!isPreGameweekOne}
            />
          </div>
          <TeamLineup
            starting={displayedStarting}
            bench={displayedBench}
            maxFixtures={VIEW_MODE_FIXTURE_COUNT[viewMode]}
            isPriceChangeView={viewMode === 'priceChanges'}
            isPriceChangeLoading={isPriceChangeLoading}
            getIsFixturePredictionLocked={
              isLocked
                ? (gameweek) => gameweek !== FREE_PREDICTIONS_GAMEWEEK
                : undefined
            }
            onPlayerPress={handlePlayerPress}
            selectedPlayerId={swapSource?.player.id ?? null}
            incomingPlayerIds={stagedIncomingIds}
            outgoingPlayerIds={stagedOutgoingIds}
            forceShowQuickActions={isEditModeActive}
            onMakeCaptain={isInTransferMode ? undefined : handleQuickCaptain}
            onMakeViceCaptain={
              isInTransferMode ? undefined : handleQuickViceCaptain
            }
            onTransferOut={handleQuickTransferOut}
            onCancelTransferOut={handleCancelTransferOut}
            onEmptySlotPress={() =>
              playerListRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
              })
            }
          />
          {transferMode.stagedTransfers.length > 0 && (
            <TransferCommitBar
              stagedCount={transferMode.stagedTransfers.length}
              readyCount={
                transferMode.stagedTransfers.filter(
                  (transfer) => transfer.incoming !== null
                ).length
              }
              projectedBank={transferMode.projectedBank}
              canCommit={transferMode.canCommit}
              isCommitting={commitTransfers.isLoading}
              onCommit={handleCommitTransfers}
              onReset={() => {
                transferMode.reset();
                setIsEditModeActive(false);
              }}
            />
          )}
          <ActionButtons
            onOptimise={handleOptimise}
            onEditTeam={handleToggleEditTeam}
            onResetTeam={handleResetTeam}
            isOptimising={optimiseLineup.isLoading}
            isDisabled={isMutationPending}
            isEditModeActive={isEditModeActive}
            isOptimiseLocked={isLocked}
          />
          <ChipBanner
            chipSelections={chipSelections}
            isLocked={isLocked}
            onSelectChip={
              isLocked || isInTransferMode
                ? () => undefined
                : setSelectedChipType
            }
          />
        </div>
        <div
          ref={playerListRef}
          // Once a player is transferred out, the empty "tap to add" slot
          // points here for a replacement. Highlight the panel so the next
          // step is obvious on desktop (where it sits beside the pitch and a
          // scroll is a no-op) as well as mobile.
          className={`scroll-mt-4 rounded-lg transition-shadow ${
            isPickingReplacement ? 'ring-2 ring-primary-500 ring-offset-2' : ''
          }`}
        >
          {isPickingReplacement && (
            <p
              className="mb-2 text-sm font-semibold text-primary-600"
              data-cy="pick-replacement-prompt"
            >
              Pick a replacement
              {transferMode.pendingPositions.length > 0
                ? ` (${transferMode.pendingPositions.join(' or ')})`
                : ''}
            </p>
          )}
          <PlayerListPanel
            selectedGameweek={selectedGameweek}
            bank={selectedGameweekData.bank}
            replacementBudget={transferMode.projectedBank}
            onSelectPlayer={
              isPickingReplacement ? transferMode.transferIn : undefined
            }
            onShowPlayerInfo={handleShowPlayerInfo}
            requiredPositions={transferMode.pendingPositions}
            disabledPlayerIds={disabledPlayerIds}
            arePredictionsLocked={isLocked}
          />
        </div>
      </div>

      <PlayerInfoModal
        subject={
          actionTarget
            ? toInfoSubject(actionTarget.player)
            : infoOnlyPlayer
            ? listPlayerToInfoSubject(infoOnlyPlayer)
            : null
        }
        startGameweek={selectedGameweek}
        onClose={() => {
          setActionTarget(null);
          setInfoOnlyPlayer(null);
        }}
        actions={
          actionTarget
            ? {
                isCaptain: actionTarget.player.isCaptain,
                isViceCaptain: actionTarget.player.isViceCaptain,
                isPending: isMutationPending,
                isTransferModeActive: isInTransferMode,
                onMakeCaptain: () => handleMakeCaptain(actionTarget),
                onMakeViceCaptain: () => handleMakeViceCaptain(actionTarget),
                onStartSubstitution: () =>
                  handleStartSubstitution(actionTarget),
                onTransferOut: () => handleTransferOut(actionTarget),
                canRecommendTransfer: nextGameweekSquadIds.has(
                  actionTarget.player.id
                ),
                onRecommendTransfer: () => handleRecommendTransfer(actionTarget)
              }
            : null
        }
        isPayingMember={isPayingMember}
      />

      <ResetTeamModal
        isOpen={isResetTeamOpen}
        onClose={() => setIsResetTeamOpen(false)}
        onReset={handleConfirmReset}
        isResetting={importFromFpl.isLoading}
        onSwitchTeam={() => {
          setIsResetTeamOpen(false);
          setIsImportTeamOpen(true);
        }}
      />

      <UpdateBankModal
        isOpen={isEditBankOpen}
        onClose={() => setIsEditBankOpen(false)}
        initialBank={selectedGameweekData.bank}
        isUpdating={setBank.isLoading}
        onUpdate={async (bank) => {
          await setBank.mutateAsync({ gameweek: selectedGameweek, bank });
        }}
      />

      <TeamImportModal
        isOpen={isImportTeamOpen}
        onClose={() => setIsImportTeamOpen(false)}
        onImport={handleResetTeamImport}
        isImporting={importFromFpl.isLoading}
        title="Import a different team"
        description="Enter your team name or FPL ID to import a new squad. This will replace your current team."
      />

      <TransferErrorModal
        isOpen={errorMessages.length > 0 || transferBlockMessage !== null}
        title={
          transferBlockMessage ? 'Too many players from one club' : errorTitle
        }
        messages={transferBlockMessage ? [transferBlockMessage] : errorMessages}
        onDismiss={() => {
          clearMutationErrors();
          setTransferBlockMessage(null);
        }}
      />

      <TransferHitModal
        isOpen={pendingHitTransfers !== null}
        hit={transferHit}
        onConfirm={handleConfirmHit}
        onDismiss={() => setPendingHitTransfers(null)}
        isConfirming={commitTransfers.isLoading}
      />

      <ChipModal
        chipType={selectedChipType}
        scheduledGameweek={
          selectedChipType
            ? chipSelections[selectedChipType].selectedGameweek
            : null
        }
        gameweekOptions={chipGameweekOptions}
        isPending={activateChip.isLoading || deactivateChip.isLoading}
        onClose={() => setSelectedChipType(null)}
        onSave={handleSaveChip}
      />
    </div>
  );
};
